import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Button } from '@/shared/ui/button';
import { Textarea } from '@/shared/ui/textarea';
import { Label } from '@/shared/ui/label';
import { Loader2 } from 'lucide-react';

const MAX_REASON_LENGTH = 500;

interface Props {
  open: boolean;
  /** User-facing order identifier (e.g. "2026-04-24-001" or "#ABCDEF"). */
  orderIdentifier: string;
  submitting: boolean;
  /** "reject" for PENDING orders, "cancel" for already-accepted orders. Defaults to "reject". */
  mode?: 'reject' | 'cancel';
  onConfirm: (reason: string | undefined) => void;
  onOpenChange: (open: boolean) => void;
}

export function RejectOrderDialog({
  open,
  orderIdentifier,
  submitting,
  mode = 'reject',
  onConfirm,
  onOpenChange,
}: Props) {
  const { t } = useTranslation();
  const verb = t(mode === 'cancel' ? 'orders.cancelVerb' : 'orders.rejectVerb');
  const verbLower = t(mode === 'cancel' ? 'orders.cancelVerbLower' : 'orders.rejectVerbLower');
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (open) setReason('');
  }, [open]);

  const handleConfirm = () => {
    const trimmed = reason.trim();
    onConfirm(trimmed.length > 0 ? trimmed : undefined);
  };

  const remaining = MAX_REASON_LENGTH - reason.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('orders.rejectTitle', { verb, identifier: orderIdentifier })}</DialogTitle>
          <DialogDescription>
            {t('orders.rejectOptionalReason', { verbLower })}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 py-2">
          <Label htmlFor="reject-reason" className="text-xs text-muted-foreground">
            {t('orders.reasonLabel')}
          </Label>
          <Textarea
            id="reject-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value.slice(0, MAX_REASON_LENGTH))}
            placeholder={t('orders.rejectReasonPlaceholder')}
            maxLength={MAX_REASON_LENGTH}
            rows={4}
            disabled={submitting}
          />
          <div className="flex justify-end text-[11px] text-muted-foreground">
            {remaining} / {MAX_REASON_LENGTH}
          </div>
        </div>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <Button variant="destructive" onClick={handleConfirm} disabled={submitting}>
            {submitting && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
            {t('orders.rejectOrder', { verb })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
